import { getFormattedPrice } from '../../../../../sharedModules/getFormattedPrice';
import type { Deal, Promo } from '../../../../types/Deal';
import getPriceType from '../../../../utils/getPriceType';
import { getPercentageSaving } from '../../../getPercentageSaving';
import type Localiser from '../../../Localiser';

interface Props {
  showWasPrice?: string;
  localiser: Localiser;
  deal: Deal;
}

const hasPromo = (promos: Promo[] | undefined, type: string): boolean =>
  Boolean(promos?.some((promo) => promo.type === type));

const getSavingValue = (deal: Deal, localiser: Props['localiser']): string | null => {
  const { offer } = deal;
  const savingValue = (Number(offer.was_price) - Number(offer.price)).toFixed(2);

  return getFormattedPrice({
    price: savingValue,
    currencyIso: offer.currency_iso,
    showOfferLabel: false,
    type: getPriceType(deal),
    locale: localiser.language,
    translate: localiser.translate,
    showPriceRounded: 'no',
  });
};

export const addWasPricePromos = ({ showWasPrice, localiser, deal }: Props): Promo[] => {
  if (!deal) return deal;

  const { offer, promos } = deal;
  const dealPromos = promos ?? [];

  if (!offer?.was_price) return dealPromos;

  const percentage = getPercentageSaving({
    price: deal.offer.price,
    wasPrice: deal.offer.was_price,
  });

  switch (showWasPrice) {
    case 'promoPercentage':
    case 'allPercentage': {
      if (hasPromo(promos, 'was-price-percentage')) {
        return dealPromos;
      }
      if (!percentage) {
        return dealPromos;
      }

      const value = localiser.translate('percentageDiscount', [percentage]);
      const newPromo: Promo = {
        type: 'was-price-percentage',
        value,
        display_value: value,
      };

      return [newPromo, ...dealPromos];
    }

    case 'promoSaving':
    case 'allSaving': {
      if (hasPromo(promos, 'was-price-light')) {
        return dealPromos;
      }
      if (!percentage) {
        return dealPromos;
      }

      const formattedSaving = getSavingValue(deal, localiser);
      const value = localiser.translate('priceDiscount', [formattedSaving || '']);
      const newPromo: Promo = {
        type: 'was-price-light',
        value,
        display_value: value,
      };

      return [...dealPromos, newPromo];
    }

    case 'promoPercentageAndSaving': {
      if (hasPromo(promos, 'was-price-percentage-saving')) {
        return dealPromos;
      }
      if (!percentage) {
        return dealPromos;
      }

      const formattedSaving = getSavingValue(deal, localiser);
      const value = `${localiser.translate('percentageDiscount', [
        percentage,
      ])} (${formattedSaving})`;

      const newPromo: Promo = {
        type: 'was-price-percentage-saving',
        value,
        display_value: value,
      };

      return [newPromo, ...dealPromos];
    }

    default:
      return dealPromos;
  }
};
